import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useThree, createPortal, useFrame, extend, applyProps } from '@react-three/fiber';
import { WebGLCubeRenderTarget, Scene, HalfFloatType } from 'three';
import { GroundProjectedEnv as GroundProjectedEnvImpl } from 'three-stdlib';
import { useEnvironment } from '@react-three/drei';
const isRef = (obj) => obj.current && obj.current.isScene;
const resolveScene = (scene) => (isRef(scene) ? scene.current : scene);
function setEnvProps(background, scene, defaultScene, texture, sceneProps = {}) {
    // defaults
    sceneProps = {
        backgroundBlurriness: sceneProps.blur ?? 0,
        backgroundIntensity: 1,
        backgroundRotation: [0, 0, 0],
        environmentIntensity: 1,
        environmentRotation: [0, 0, 0],
        ...sceneProps,
    };
    const target = resolveScene(scene || defaultScene);
    const oldbg = target.background;
    const oldenv = target.environment;
    const oldSceneProps = {
        // @ts-ignore
        backgroundBlurriness: target.backgroundBlurriness,
        // @ts-ignore
        backgroundIntensity: target.backgroundIntensity,
        // @ts-ignore
        backgroundRotation: target.backgroundRotation?.clone?.() ?? [0, 0, 0],
        // @ts-ignore
        environmentIntensity: target.environmentIntensity,
        // @ts-ignore
        environmentRotation: target.environmentRotation?.clone?.() ?? [0, 0, 0],
    };
    if (background !== 'only')
        target.environment = texture;
    if (background)
        target.background = texture;
    applyProps(target, sceneProps);
    return () => {
        if (background !== 'only')
            target.environment = oldenv;
        if (background)
            target.background = oldbg;
        applyProps(target, oldSceneProps);
    };
}
export function EnvironmentMap({ scene, background = false, map, ...config }) {
    const defaultScene = useThree((state) => state.scene);
    React.useLayoutEffect(() => {
        if (map)
            return setEnvProps(background, scene, defaultScene, map, config);
    });
    return null;
}
export function EnvironmentCube({ background = false, scene, blur, backgroundBlurriness, backgroundIntensity, backgroundRotation, environmentIntensity, environmentRotation, ...rest }) {
    const texture = useEnvironment(rest);
    const defaultScene = useThree((state) => state.scene);
    React.useLayoutEffect(() => {
        return setEnvProps(background, scene, defaultScene, texture, {
            blur,
            backgroundBlurriness,
            backgroundIntensity,
            backgroundRotation,
            environmentIntensity,
            environmentRotation,
        });
    });
    return null;
}
export function EnvironmentPortal({ children, near = 1, far = 1000, resolution = 256, frames = 1, map, background = false, blur, backgroundBlurriness, backgroundIntensity, backgroundRotation, environmentIntensity, environmentRotation, scene, files, path, preset = undefined, renderPriority, extensions, }) {
    const gl = useThree((state) => state.gl);
    const defaultScene = useThree((state) => state.scene);
    const camera = React.useRef(null);
    const [virtualScene] = React.useState(() => new Scene());
    const fbo = React.useMemo(() => {
        const fbo = new WebGLCubeRenderTarget(resolution);
        fbo.texture.type = HalfFloatType;
        return fbo;
    }, [resolution]);
    React.useLayoutEffect(() => {
        if (frames === 1)
            camera.current.update(gl, virtualScene);
        return setEnvProps(background, scene, defaultScene, fbo.texture, {
            blur,
            backgroundBlurriness,
            backgroundIntensity,
            backgroundRotation,
            environmentIntensity,
            environmentRotation,
        });
    }, [children, virtualScene, fbo.texture, scene, defaultScene, background, frames, gl]);
    let count = 1;
    useFrame(({ gl: renderer, camera: cam, scene }) => {
        if (frames === Infinity || count < frames) {
            camera.current.update(gl, virtualScene);
            count++;
            renderer.render(scene, cam);
        }
    }, renderPriority);
    return (_jsx(_Fragment, { children: createPortal(_jsxs(_Fragment, { children: [children, _jsx("cubeCamera", { ref: camera, args: [near, far, fbo] }), files || preset ? (_jsx(EnvironmentCube, { background: true, files: files, preset: preset, path: path, extensions: extensions })) : map ? (_jsx(EnvironmentMap, { background: true, map: map, extensions: extensions })) : null] }), virtualScene) }));
}
const GroundProjectedEnvironment = extend(GroundProjectedEnvImpl);
function EnvironmentGround(props) {
    const textureDefault = useEnvironment(props);
    const texture = props.map || textureDefault;
    const args = React.useMemo(() => [texture], [texture]);
    const height = props.ground?.height;
    const radius = props.ground?.radius;
    const scale = props.ground?.scale ?? 1000;
    return (_jsxs(_Fragment, { children: [_jsx(EnvironmentMap, { ...props, map: texture }), _jsx(GroundProjectedEnvironment, { args: args, scale: scale, height: height, radius: radius })] }));
}
export function CustomEnvironment(props) {
    return props.ground ? (_jsx(EnvironmentGround, { ...props })) : props.map ? (_jsx(EnvironmentMap, { ...props })) : props.children ? (_jsx(EnvironmentPortal, { ...props })) : (_jsx(EnvironmentCube, { ...props }));
}
